import * as React from 'react'
import { Outlet } from 'react-router-dom'

import { ApolloClient } from '@apollo/client'
import {
  FetchEmployeesSettingsDocument,
  FetchEmployeesSettingsQuery,
} from 'generated/resources/graphql'
import capitalize from 'lodash/capitalize'

import i18n from 'modules/core/lib/i18n'
import { NavigationRoutesFn } from 'modules/core/lib/react-router'

import { TabTypes } from 'modules/attendance/components/SettingItemPage/TimeTracking/tabs'
import { AttendanceFeaturesProvider } from 'modules/attendance/lib/attendanceFeaturesContext'
import RedirectToPolicySettingsPage from 'modules/attendance/pages/settings/RedirectToPolicySettingsPage'
import { getSettingPageMeta } from 'modules/settings/lib/getSettingPageMeta'

const RootPage = React.lazy(() => import('modules/attendance/pages/settings/RootPage'))
const FaceRecognitionLogsPage = React.lazy(
  () =>
    import(
      'modules/attendance/components/SettingItemPage/TimeTracking/FaceRecognitionLogs/FaceRecognitionLogsPage'
    )
)
const CustomTimeRangesPage = React.lazy(
  () => import('modules/attendance/pages/CustomTimeRangesPage')
)
const TimeTrackingSettings = React.lazy(() => import('modules/attendance/pages/settings/Settings'))
const SettingsItemPage = React.lazy(
  () => import('modules/attendance/pages/settings/SettingsItemPage')
)

const getPolicyName = (settingId: number, watchQuery: ApolloClient<unknown>['watchQuery']) => {
  return watchQuery({
    query: FetchEmployeesSettingsDocument,
    variables: {
      ids: [settingId],
    },
  }).map(({ data }: { data: FetchEmployeesSettingsQuery }) => {
    const employeesSettings = data?.attendance.employeesSettingsConnection.nodes ?? []
    const employeesSetting = !!employeesSettings.length ? employeesSettings[0] : undefined
    const policyName = employeesSetting?.attendanceEmployeesPolicy?.name ?? ''

    return {
      title: policyName,
    }
  })
}

const getTabTitle = (tabId: string) => {
  switch (tabId) {
    case TabTypes.edit:
      return i18n.t('time_tracking.settings.tabs.policy_settings')
    case TabTypes.work_time_categories:
      return i18n.t('time_tracking.settings.tabs.work_time_categories')
    case TabTypes.compensation:
      return i18n.t('time_tracking.settings.tabs.compensation')
    case TabTypes.breaks:
      return i18n.t('time_tracking.settings.tabs.breaks')
    case TabTypes.preferences:
      return i18n.t('time_tracking.settings.tabs.preferences')
    case TabTypes.employees:
      return i18n.t('time_tracking.settings.tabs.employees')
    default:
      return capitalize(tabId)
  }
}
const settingsRoutes: NavigationRoutesFn = (navigation) => {
  return [
    {
      path: navigation.settings.time_tracking.path,
      element: (
        <AttendanceFeaturesProvider>
          <RootPage />
        </AttendanceFeaturesProvider>
      ),
      handle: {
        meta: () => getSettingPageMeta(navigation, 'time_tracking'),
      },
      children: [
        {
          index: true,
          element: <TimeTrackingSettings />,
        },
        {
          path: navigation.settings.time_tracking.settingId.path,
          element: <Outlet />,
          handle: {
            meta: ({ params, watchQuery }) => getPolicyName(Number(params.settingId), watchQuery),
          },
          children: [
            {
              index: true,
              element: <RedirectToPolicySettingsPage />,
            },
            {
              path: navigation.settings.time_tracking.settingId.tabId.path,
              element: <SettingsItemPage />,
              handle: {
                meta: ({ params }) => {
                  const { tabId = '' } = params ?? {}
                  const title = getTabTitle(tabId)
                  return { title }
                },
              },
            },
            {
              path: navigation.settings.time_tracking.settingId
                .employeesEmployeeIdFaceRecognitionLogs.path,
              element: <FaceRecognitionLogsPage />,
            },
            {
              path: navigation.settings.time_tracking.settingId.timeRanges.path,
              element: <CustomTimeRangesPage />,
              handle: {
                meta: () => ({
                  title: i18n.t('time_tracking.settings.time_blocks.work_time_categorization'),
                }),
              },
            },
          ],
        },
      ],
    },
  ]
}

export default settingsRoutes
